import React, { useState, useRef, useEffect } from 'react';
import { useSearch } from '../../contexts/SearchContext';
import { useDebounce } from '../../hooks/useDebounce';
import SearchResultsWithQuery from './SearchResultsWithQuery';
import api from '../../services/api';

// Image search is disabled until a vision provider (Claude API key / free alternative) is set up.
// Flip back to true to re-enable the "Image Search" button once that's ready.
const SHOW_IMAGE_SEARCH = false;

const SearchBarWithQuery = ({ className, placeholder }) => {
  const { 
    searchQuery, 
    setSearchQuery, 
    showResults, 
    setShowResults, 
    clearSearch,
    recentSearches,
    addToRecentSearches 
  } = useSearch();
  
  const [isFocused, setIsFocused] = useState(false);
  const [localQuery, setLocalQuery] = useState(searchQuery);
  const [isUploading, setIsUploading] = useState(false);
  const searchInputRef = useRef(null);
  const searchContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  // Tracks the last value this component pushed into context, so the sync
  // effect below can tell "context changed because we typed" apart from
  // "context changed because something else navigated/searched" and only
  // overwrite the input box in the latter case.
  const lastEmittedQueryRef = useRef(searchQuery);

  // Typing effect state
  const placeholders = [
    "Search products...",
    "Search dental products...",
    "Search skincare products...",
    "Search surgical products..."
  ];
  const [placeholderText, setPlaceholderText] = useState("");
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    let timeout;
    const typingSpeed = 70;
    const deletingSpeed = 40;
    const pauseTime = 1500;

    const currentPlaceholder = placeholders[placeholderIndex];

    if (isDeleting) {
      if (placeholderText.length > 0) {
        timeout = setTimeout(() => {
          setPlaceholderText(currentPlaceholder.substring(0, placeholderText.length - 1));
        }, deletingSpeed);
      } else {
        setIsDeleting(false);
        setPlaceholderIndex((prev) => (prev + 1) % placeholders.length);
      }
    } else {
      if (placeholderText.length < currentPlaceholder.length) {
        timeout = setTimeout(() => {
          setPlaceholderText(currentPlaceholder.substring(0, placeholderText.length + 1));
        }, typingSpeed);
      } else {
        timeout = setTimeout(() => {
          setIsDeleting(true);
        }, pauseTime);
      }
    }

    return () => clearTimeout(timeout);
  }, [placeholderText, isDeleting, placeholderIndex]);

  // Debounce the search query by 300ms
  const debouncedQuery = useDebounce(localQuery, 300);

  // Update search context when debounced query changes
  useEffect(() => {
    lastEmittedQueryRef.current = debouncedQuery;
    setSearchQuery(debouncedQuery);
  }, [debouncedQuery, setSearchQuery]);

  // Keep the input box in sync when the query changes from outside this
  // component (e.g. landing on /search?q=... directly, clicking a recent or
  // related search, browser back/forward) - otherwise it's left showing the
  // placeholder while a search is already active.
  useEffect(() => {
    if (searchQuery !== lastEmittedQueryRef.current) {
      lastEmittedQueryRef.current = searchQuery;
      setLocalQuery(searchQuery);
    }
  }, [searchQuery]);

  // Handle clicks outside the search component
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setShowResults]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setLocalQuery(value);
    
    // Show results if we have a query or recent searches
    if (value.trim().length >= 2 || recentSearches.length > 0) {
      setShowResults(true);
    } else if (value.trim() === '') {
      setShowResults(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (localQuery.trim()) {
      // Add to recent searches when form is submitted
      addToRecentSearches(localQuery.trim());
      setShowResults(false);
      // Navigate to search results page
      window.location.href = `/search?q=${encodeURIComponent(localQuery.trim())}`;
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (localQuery.trim().length >= 2 || recentSearches.length > 0) {
      setShowResults(true);
    }
  };

  const handleClear = () => {
    setLocalQuery('');
    setSearchQuery('');
    clearSearch();
    searchInputRef.current.focus();
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('image', file);

      const response = await api.post('/search/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const identifiedQuery = response.data?.data?.searchQuery;
      if (identifiedQuery) {
        setLocalQuery(identifiedQuery);
        addToRecentSearches(identifiedQuery);
        setShowResults(true);
      } else {
        alert("Couldn't identify a product in that image. Try a clearer photo.");
      }
    } catch (error) {
      console.error('Image search failed', error);
      alert('Failed to process image search');
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div 
      className={`relative w-full max-w-3xl mx-auto ${className || ''}`}
      ref={searchContainerRef}
    >
      <form onSubmit={handleSubmit} className="w-full">
        <div className="relative flex items-center">
          <input
            type="text"
            ref={searchInputRef}
            placeholder={isUploading ? "Analyzing image..." : (placeholder || `${placeholderText}|`)}
            value={localQuery}
            onChange={handleInputChange}
            onFocus={handleFocus}
            className={`w-full py-2.5 pl-10 pr-32 rounded-full text-gray-700 
                      focus:outline-none focus:ring-1 focus:ring-primary-500 
                      border border-gray-300 bg-white shadow-sm transition-shadow
                      ${isFocused ? 'ring-1 ring-primary-500 shadow-md' : ''}`}
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg
              className="h-5 w-5 text-gray-400"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center gap-2">
            {localQuery && (
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600 transition-colors"
                onClick={handleClear}
                title="Clear search"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            {SHOW_IMAGE_SEARCH && !localQuery && (
              <>
                <button
                  type="button"
                  className="flex items-center text-gray-400 hover:text-gray-600 text-xs italic gap-1 transition-colors"
                  title="Image Search"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                >
                  {isUploading ? "Uploading..." : "Image Search"}
                  {!isUploading && (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  )}
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/*"
                  className="hidden"
                />
              </>
            )}
          </div>
        </div>
      </form>
      
      {showResults && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1">
          <SearchResultsWithQuery 
            searchQuery={debouncedQuery} 
            onClose={() => setShowResults(false)} 
          />
        </div>
      )}
    </div>
  );
};

export default SearchBarWithQuery;
